import { useState, useEffect, useRef } from "react"
import { BreadCrumb } from "primereact/breadcrumb"
import { InputText } from "primereact/inputtext"
import { DataView } from "primereact/dataview"
import { Button } from "primereact/button"
import httpFloresta from "../../plugins/httpBot"
import Loading from "../../components/Loading"

interface SearchResult {
	name: string
	size?: number
	path: string[]
	isDirectory: boolean
}

interface IFile {
	name: string
	size: number
}

interface PaginatedResponse {
	data: (IFile | string)[]
	pages: number
	page: number
	totalPages: number
	currentPage: number
	hasMore: boolean
}

const DadosFlorestaPage = () => {
	const [currentPath, setCurrentPath] = useState<string[]>([])
	const [searchTerm, setSearchTerm] = useState("")
	const [currentItems, setCurrentItems] = useState<(IFile | string)[]>([])
	const [globalSearchResults, setGlobalSearchResults] = useState<
		SearchResult[]
	>([])
	const [isGlobalSearch, setIsGlobalSearch] = useState(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [page, setPage] = useState(1)
	const [hasMore, setHasMore] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [currentDirectory, setCurrentDirectory] = useState<string | null>(null)
	const [totalPages, setTotalPages] = useState(1)

	const mounted = useRef(true)

	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	async function fetchDirectories(pageNumber = 1, append = false) {
		try {
			if (pageNumber === 1) {
				setLoading(true)
			} else {
				setLoadingMore(true)
			}

			const response = await httpFloresta.get(`api/directories`, {
				params: {
					page: pageNumber,
					pageSize: 20,
				},
			})

			const paginatedData = response.data as PaginatedResponse

			if (mounted.current) {
				if (append) {
					setCurrentItems((prev) => [...prev, ...paginatedData.data])
				} else {
					setCurrentItems(paginatedData.data)
				}
				setTotalPages(paginatedData.pages)
				setPage(paginatedData.page)
			}
		} catch (error) {
			console.error(error)
		} finally {
			if (mounted.current) {
				setLoading(false)
				setLoadingMore(false)
			}
		}
	}

	async function fetchFiles(directory: string, pageNumber = 1, append = false) {
		try {
			if (pageNumber === 1) {
				setLoading(true)
			} else {
				setLoadingMore(true)
			}

			const response = await httpFloresta.get(`api/directories/${directory}`, {
				params: {
					page: pageNumber,
					pageSize: 20,
				},
			})

			const paginatedData = response.data as PaginatedResponse

			if (mounted.current) {
				if (append) {
					setCurrentItems((prev) => [...prev, ...paginatedData.data])
				} else {
					setCurrentItems(paginatedData.data)
				}
				setTotalPages(paginatedData.pages)
				setPage(paginatedData.page)
			}
		} catch (error) {
			console.error(error)
		} finally {
			if (mounted.current) {
				setLoading(false)
				setLoadingMore(false)
			}
		}
	}

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const element = e.target as HTMLDivElement
		const scrollPosition = element.scrollTop + element.clientHeight
		const threshold = 100
		const isNearBottom = element.scrollHeight - scrollPosition < threshold

		if (isNearBottom && page < totalPages && !loadingMore && !loading) {
			const nextPage = page + 1
			if (currentDirectory) {
				fetchFiles(currentDirectory, nextPage, true)
			} else {
				fetchDirectories(nextPage, true)
			}
		}
	}

	async function downloadFile(
		filePath: string[],
		fileName: string,
	): Promise<void> {
		try {
			const fullPath = [...filePath, fileName].join("/")

			setLoading(true)

			const response = await httpFloresta.get(`api/directories/${fullPath}`, {
				responseType: "blob",
			})

			const blob = new Blob([response.data], {
				type: response.headers["content-type"] || "application/octet-stream",
			})

			const url = window.URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = url
			link.setAttribute("download", fileName)
			document.body.appendChild(link)
			link.click()

			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)

			return response.data
		} catch (error) {
			console.error("Error downloading file:", error)
			throw new Error("Failed to download file")
		} finally {
			setLoading(false)
		}
	}

	const handleDownload = async (file: IFile) => {
		try {
			await downloadFile(currentPath, file.name)
		} catch (error) {
			console.error("Download failed:", error)
		}
	}

	const handleSearch = (value: string) => {
		setSearchTerm(value)
	}

	useEffect(() => {
		if (currentPath.length === 0) {
			setCurrentDirectory(null)
			setPage(1)
			fetchDirectories(1, false)
		}
	}, [currentPath])

	const navigateTo = (index: number) => {
		setCurrentPath((prev) => prev.slice(0, index + 1))
	}

	const handleItemClick = (item: string | IFile) => {
		if (typeof item === "string") {
			setCurrentPath((prev) => [...prev, item])
			setPage(1)
			setCurrentDirectory(item)
			fetchFiles(item, 1, false)
		} else {
			handleDownload(item)
		}
	}

	const formatFileSize = (bytes: number) => {
		const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
		if (bytes === 0) return "0 Byte"
		const i = Math.floor(Math.log(bytes) / Math.log(1024))
		return Math.round(bytes / Math.pow(1024, i)) + " " + sizes[i]
	}

	const breadcrumbItems = [
		{ label: "Arquivos", command: () => setCurrentPath([]) },
		...currentPath.map((path, index) => ({
			label: path,
			command: () => navigateTo(index),
		})),
	]

	const homeItem = { icon: "pi pi-home", url: "/home" }

	const filteredItems = currentItems.filter((item) => {
		const name = typeof item === "string" ? item : item.name
		return name.toLowerCase().includes(searchTerm.toLowerCase())
	})

	const itemTemplate = (item: string | IFile) => {
		const isDirectory = typeof item === "string"

		return (
			<div
				className='flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b transition-colors duration-150'
				onClick={() => handleItemClick(item)}
			>
				<i
					className={`pi ${isDirectory ? "pi-folder text-yellow-400" : "pi-file text-blue-400"} text-xl mr-3`}
				></i>

				<div className='flex-1'>
					<div className='font-medium'>
						{isDirectory ? item : (item as IFile).name}
					</div>
					{!isDirectory && (
						<div className='text-sm text-gray-500'>
							{formatFileSize((item as IFile).size)}
						</div>
					)}
				</div>

				{!isDirectory && (
					<Button
						icon='pi pi-download'
						severity='secondary'
						text
						rounded
						className='p-button-sm hover:bg-gray-100'
						tooltip='Download'
						tooltipOptions={{ position: "left" }}
						onClick={(e) => {
							e.stopPropagation()
							handleItemClick(item)
						}}
					/>
				)}
			</div>
		)
	}

	return (
		<div className='p-10 relative'>
			<BreadCrumb model={breadcrumbItems} home={homeItem} className='mb-4' />

			<div className='flex flex-col gap-4'>
				<div className='flex justify-between items-center'>
					<h1 className='text-2xl font-bold'>Explorador de Arquivos</h1>
				</div>

				<div className='bg-white p-4 rounded-lg shadow-sm'>
					<div className='mb-4'>
						<div className='relative'>
							<i className='pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10' />
							<InputText
								value={searchTerm}
								onChange={(e) => handleSearch(e.target.value)}
								placeholder='Pesquisar em todas as pastas...'
								className='w-full pl-10 py-2 border rounded-lg'
							/>
						</div>
					</div>

					<div
						className='border rounded-lg'
						style={{
							height: "600px",
							overflowY: "auto",
							position: "relative",
						}}
						onScroll={handleScroll}
					>
						{loading && (
							<div className='h-36 grid place-content-center p-20'>
								<Loading color='#212529' size='w-10' />
							</div>
						)}

						{!loading && (
							<>
								<DataView
									value={filteredItems}
									itemTemplate={itemTemplate}
									className='border-none'
									emptyMessage={"nenhum item encontrado"}
								/>

								{loadingMore && (
									<div className='p-4 flex justify-center'>
										<Loading color='#212529' size='w-6' />
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export default DadosFlorestaPage
